<template>
  <div class="flex flex-col h-full">
    <!-- Add Task Form -->
    <div v-if="showForm" class="p-4 border-b border-n-weak space-y-3">
      <input
        v-model="newTask.title"
        type="text"
        placeholder="Título da tarefa..."
        class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      />
      <div class="flex gap-2">
        <select
          v-model="newTask.type"
          class="flex-1 pl-3 pr-6 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
        >
          <option value="follow_up">📞 Follow-up</option>
          <option value="call">📱 Ligação</option>
          <option value="email">✉️ Email</option>
          <option value="meeting">🤝 Reunião</option>
          <option value="reminder">🔔 Lembrete</option>
          <option value="other">📋 Outro</option>
        </select>
        <input
          v-model="newTask.dueDate"
          type="datetime-local"
          class="flex-1 px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm"
        />
      </div>
      <textarea
        v-model="newTask.notes"
        placeholder="Observações (opcional)..."
        rows="2"
        class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-surface-1 text-sm resize-none"
      ></textarea>
      <div class="flex gap-2 justify-end">
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-md border border-n-weak bg-transparent text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer"
          @click="cancelForm"
        >
          Cancelar
        </button>
        <button
          class="button success"
          :disabled="!newTask.title.trim()"
          @click="saveTask"
        >
          {{ editingTaskId ? 'Atualizar' : 'Criar Tarefa' }}
        </button>
      </div>
    </div>

    <!-- Tasks List -->
    <div class="flex-1 overflow-y-auto">
      <div v-if="sortedTasks.length === 0" class="flex flex-col items-center justify-center py-12 text-n-slate-10">
        <span class="text-4xl mb-3">📋</span>
        <p class="text-sm m-0">Nenhuma tarefa criada</p>
        <p class="text-xs m-0 mt-1">Clique em "Nova Tarefa" para começar</p>
      </div>

      <div v-else class="divide-y divide-n-weak">
        <div
          v-for="task in sortedTasks"
          :key="task.id"
          class="flex items-start gap-3 px-4 py-3 hover:bg-n-alpha-1 transition-colors group"
          :class="{ 'opacity-50': task.done }"
        >
          <!-- Checkbox -->
          <button
            v-tooltip.top="task.done ? 'Reabrir tarefa' : 'Concluir tarefa'"
            class="mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all"
            :class="task.done
              ? 'bg-n-blue-9 border-n-blue-9'
              : isOverdue(task)
                ? 'border-n-ruby-9 bg-n-surface-1 hover:bg-n-ruby-3'
                : 'border-n-slate-9 bg-n-surface-1 hover:bg-n-blue-3 hover:border-n-blue-9'"
            @click="toggleTask(task.id)"
          >
            <span v-if="task.done" class="text-white text-xs font-bold">✓</span>
          </button>

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm">{{ typeEmoji(task.type) }}</span>
              <span
                class="text-sm font-medium truncate"
                :class="task.done ? 'line-through text-n-slate-10' : 'text-n-slate-12'"
              >
                {{ task.title }}
              </span>
            </div>
            <div class="flex items-center gap-2 mt-1">
              <span
                v-if="task.dueDate"
                class="text-xs px-1.5 py-0.5 rounded"
                :class="dueDateClass(task)"
              >
                {{ formatDueDate(task.dueDate) }}
              </span>
              <span v-if="task.notes" class="text-xs text-n-slate-10 truncate">{{ task.notes }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              class="p-1 rounded hover:bg-n-alpha-2 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-n-slate-12"
              title="Editar"
              @click="editTask(task)"
            >
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button
              class="p-1 rounded hover:bg-red-500/20 border-none bg-transparent cursor-pointer text-n-slate-10 hover:text-red-400"
              title="Excluir"
              @click="deleteTask(task.id)"
            >
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer: Add button -->
    <div v-if="!showForm" class="p-3 border-t border-n-weak">
      <button
        class="button success w-full flex items-center justify-center gap-2"
        @click="openNewForm"
      >
        <span class="text-base">+</span>
        Nova Tarefa
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaskList',
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update-tasks'],
  data() {
    return {
      showForm: false,
      editingTaskId: null,
      newTask: {
        title: '',
        type: 'follow_up',
        dueDate: '',
        notes: '',
      },
    };
  },
  computed: {
    sortedTasks() {
      return [...this.tasks].sort((a, b) => {
        // Completed at the bottom
        if (a.done !== b.done) return a.done ? 1 : -1;
        // Overdue first
        const aOverdue = this.isOverdue(a);
        const bOverdue = this.isOverdue(b);
        if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
        // By due date ascending
        if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      });
    },
  },
  methods: {
    typeEmoji(type) {
      const map = {
        call: '📱', email: '✉️', meeting: '🤝',
        follow_up: '📞', reminder: '🔔', other: '📋',
      };
      return map[type] || '📋';
    },
    isOverdue(task) {
      if (task.done || !task.dueDate) return false;
      return new Date(task.dueDate) < new Date();
    },
    formatDueDate(dateStr) {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = date - now;
      const diffHours = diffMs / (1000 * 60 * 60);
      const diffDays = diffMs / (1000 * 60 * 60 * 24);

      if (diffMs < 0) {
        const absDays = Math.abs(Math.ceil(diffDays));
        if (absDays === 0) return 'Atrasada (hoje)';
        return `Atrasada (${absDays}d)`;
      }
      if (diffHours < 1) return 'Em breve';
      if (diffHours < 24) return `Em ${Math.ceil(diffHours)}h`;
      if (diffDays < 7) return `Em ${Math.ceil(diffDays)}d`;
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    },
    dueDateClass(task) {
      if (task.done) return 'bg-n-alpha-2 text-n-slate-10';
      if (this.isOverdue(task)) return 'bg-red-500/20 text-red-400 font-semibold';
      const diffMs = new Date(task.dueDate) - new Date();
      const diffHours = diffMs / (1000 * 60 * 60);
      if (diffHours < 24) return 'bg-amber-500/20 text-amber-400';
      return 'bg-n-alpha-2 text-n-slate-11';
    },
    openNewForm() {
      this.editingTaskId = null;
      this.newTask = { title: '', type: 'follow_up', dueDate: '', notes: '' };
      this.showForm = true;
    },
    editTask(task) {
      this.editingTaskId = task.id;
      this.newTask = {
        title: task.title,
        type: task.type,
        dueDate: task.dueDate ? task.dueDate.slice(0, 16) : '',
        notes: task.notes || '',
      };
      this.showForm = true;
    },
    cancelForm() {
      this.showForm = false;
      this.editingTaskId = null;
    },
    saveTask() {
      if (!this.newTask.title.trim()) return;

      let updated = [...this.tasks];
      if (this.editingTaskId) {
        const idx = updated.findIndex(t => t.id === this.editingTaskId);
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            title: this.newTask.title.trim(),
            type: this.newTask.type,
            dueDate: this.newTask.dueDate || null,
            notes: this.newTask.notes.trim(),
          };
        }
      } else {
        updated.push({
          id: `t_${Date.now()}`,
          title: this.newTask.title.trim(),
          type: this.newTask.type,
          dueDate: this.newTask.dueDate || null,
          notes: this.newTask.notes.trim(),
          done: false,
          createdAt: new Date().toISOString(),
        });
      }

      this.$emit('update-tasks', updated);
      this.cancelForm();
    },
    toggleTask(id) {
      const updated = this.tasks.map(t =>
        t.id === id ? { ...t, done: !t.done } : t
      );
      this.$emit('update-tasks', updated);
    },
    deleteTask(id) {
      const updated = this.tasks.filter(t => t.id !== id);
      this.$emit('update-tasks', updated);
    },
  },
};
</script>
